import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useChangePassword } from "@api/hooks";
import { FORM_IDS, changePasswordSchema } from "@utils";

import { FormLayout, FormCard, FormField, FormAltLink, Button } from "@common";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

export default function Password() {
  const nav = useNavigate();
  const changePasswordMutation = useChangePassword();

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.AUTH_CHANGE_PASSWORD_LOGGED_OUT,
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(changePasswordSchema),
    mode: "onTouched",
    defaultValues: { email: "", current: "", next: "" },
  });

  async function onSubmit(values) {
    clearErrors("root");
    try {
      await changePasswordMutation.mutateAsync({
        email: values.email,
        old_password: values.current,
        new_password: values.next,
      });

      recordSubmitSuccess();

      nav("/login", {
        replace: true,
        state: { msg: "Password updated. Please sign in." },
      });
    } catch (e) {
      setError("root", { message: e?.response?.data?.message || "Unable to update password." });
    }
  }

  return (
    <FormLayout>
      <FormCard title="Change Password" onSubmit={handleSubmit(onSubmit)} {...formTrackingProps}>
        {errors.root?.message && (
          <div className="mb-4 rounded-lg border border-palette-rose-border bg-palette-rose-soft px-3 py-2 text-sm text-palette-rose-heading">
            {errors.root.message}
          </div>
        )}

        <FormField
          label="Email"
          name="email"
          type="email"
          placeholder="your@email.com"
          register={register}
          error={errors.email?.message}
        />

        <FormField
          label="Current password"
          name="current"
          type="password"
          placeholder="Your current password"
          register={register}
          error={errors.current?.message}
        />

        <FormField
          label="New password"
          name="next"
          type="password"
          placeholder="Min. 6 characters"
          register={register}
          error={errors.next?.message}
        />

        <Button as="button" type="submit" className="w-full" disabled={!isValid || isSubmitting}>
          {isSubmitting ? "Updating…" : "Update password"}
        </Button>

        <div className="mt-3">
          <FormAltLink to="/login">Back to sign in</FormAltLink>
        </div>
      </FormCard>
    </FormLayout>
  );
}
